/**
 * Badge / Tag, Chip, StateDot, LockBadge, GatedBadge, AiBadge.
 *
 * The tone vocabulary is doctrine, not palette:
 *
 *   ok / warn / danger / info   the state of a thing in the world.
 *   ai                          orchid. Something the assistant generated or proposes.
 *   gated                       orchid + a border: a policy-gated AI action that CHANGES
 *                               state (doctrine 2). Never used for emphasis.
 *   demo                        DEMO_MODE and nothing else (doctrine 7). Shares the orchid
 *                               slot deliberately — both mean "not the ordinary path".
 *   ocean / clay / solid        classification, not status: call reasons, message kinds.
 *                               Desaturated so real status keeps ownership of colour.
 *   plain                       an outlined chip on the canvas: counts, versions, "Not set".
 *   accent                      the indigo pale wash. One or two per screen at most.
 *   neutral                     everything else.
 *
 * A read-only AI action gets tone="ok" and looks calm on purpose. If a badge is orchid, an
 * operator should be able to assume something was, or would be, written somewhere.
 */
import type { ReactElement, ReactNode } from 'react';
import { Icon } from './Icon.tsx';
import type { IconName } from './Icon.tsx';

export type Tone =
  | 'ok'
  | 'warn'
  | 'danger'
  | 'info'
  | 'ai'
  | 'gated'
  | 'demo'
  | 'ocean'
  | 'clay'
  | 'solid'
  | 'plain'
  | 'accent'
  | 'neutral';

export type BadgeProps = {
  tone?: Tone;
  children: ReactNode;
  icon?: IconName;
  /** A leading filled dot, for status-at-a-glance columns. */
  dot?: boolean;
  size?: 'sm' | 'md';
  title?: string;
  className?: string;
};

export function Badge({
  tone = 'neutral',
  children,
  icon,
  dot,
  size = 'md',
  title,
  className,
}: BadgeProps): ReactElement {
  return (
    <span
      className={['nd-tag', className].filter(Boolean).join(' ')}
      data-tone={tone === 'neutral' ? undefined : tone}
      data-size={size === 'sm' ? 'sm' : undefined}
      title={title}
    >
      {dot ? <span className="nd-badge-dot" aria-hidden="true" /> : null}
      {icon ? <Icon name={icon} size={size === 'sm' ? 11 : 13} /> : null}
      {children}
    </span>
  );
}

/**
 * The same object under the name the approved sheet uses. Screens may import either;
 * `Tag` reads better where the thing being marked is a classification rather than a state.
 */
export const Tag = Badge;

/**
 * The doctrine-1 and doctrine-2 marker in badge form: a fact is fixed by policy.
 *
 * Use it beside anything the assistant is not permitted to write ("Human entry only"), any
 * switch that is enforced off for every tenant ("Locked"), and any transfer affordance. The
 * padlock is part of the meaning, so it is not optional.
 */
export function LockBadge({
  children = 'Locked',
  title,
  tone = 'plain',
  size = 'md',
}: {
  children?: ReactNode;
  title?: string;
  tone?: Tone;
  size?: 'sm' | 'md';
}): ReactElement {
  return (
    <Badge
      tone={tone}
      icon="lock"
      size={size}
      title={title ?? 'Fixed by policy — this cannot be changed from the console'}
    >
      {children}
    </Badge>
  );
}

/**
 * A policy-gated, state-changing AI action. Use it wherever one is shown — a gated tool
 * call in a transcript, a pending proposal, a button that will write to the billing system.
 */
export function GatedBadge({ children = 'Gated', title }: { children?: ReactNode; title?: string }): ReactElement {
  return (
    <Badge
      tone="gated"
      icon="lock"
      title={title ?? 'Policy-gated: this action changes state and needs an approval or a standing policy'}
    >
      {children}
    </Badge>
  );
}

/** The other half of the grammar: an AI action that only read something. */
export function ReadOnlyBadge({ children = 'Read-only' }: { children?: ReactNode }): ReactElement {
  return (
    <Badge tone="ok" icon="eye" title="Read-only: this action looked something up and changed nothing">
      {children}
    </Badge>
  );
}

/** "Generated from the incident", "Suggested by the assistant". Orchid, never indigo. */
export function AiBadge({ children = 'AI', title }: { children?: ReactNode; title?: string }): ReactElement {
  return (
    <Badge tone="ai" icon="sparkle" title={title ?? 'Generated by the assistant'}>
      {children}
    </Badge>
  );
}

export type ChipProps = {
  children: ReactNode;
  pressed?: boolean;
  onClick?: () => void;
  icon?: IconName;
  count?: number;
  title?: string;
  disabled?: boolean;
};

/** A filter toggle. Always a real button, so it is reachable and announces its state. */
export function Chip({ children, pressed = false, onClick, icon, count, title, disabled }: ChipProps): ReactElement {
  return (
    <button
      type="button"
      className="nd-chip"
      aria-pressed={pressed}
      onClick={onClick}
      title={title}
      disabled={disabled}
    >
      {icon ? <Icon name={icon} size={12} /> : null}
      {children}
      {count === undefined ? null : <span className="nd-num nd-dim">{count}</span>}
    </button>
  );
}

export type StateDotProps = {
  tone: Tone | 'muted';
  /** Required — a coloured dot with no name is decoration, not information. */
  label: string;
  /** Slow fade, for "on the line now". Off under prefers-reduced-motion. */
  pulse?: boolean;
};

/** A bare 6px status dot with an accessible name. For dense rows and nav items. */
export function StateDot({ tone, label, pulse = false }: StateDotProps): ReactElement {
  return (
    <span
      className="nd-dot"
      data-tone={tone === 'muted' || tone === 'neutral' ? undefined : tone}
      data-pulse={pulse ? 'true' : undefined}
      title={label}
      aria-label={label}
      role="img"
    />
  );
}

export default Badge;
